import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Users,
  Grid3X3,
  TrendingUp,
  Plus,
  ChevronUp,
  ChevronDown,
  Minus,
  CheckCircle,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "catalogue", icon: BookOpen,    section: "skills" },
  { id: "profile",   icon: Star,        section: "skills" },
  { id: "matrix",    icon: Grid3X3,     section: "analysis" },
  { id: "gaps",      icon: TrendingUp,  section: "analysis" },
];

const PROFICIENCY = [
  { level: 1, key: "novice",       color: "bg-red-100 text-red-700" },
  { level: 2, key: "beginner",     color: "bg-orange-100 text-orange-700" },
  { level: 3, key: "intermediate", color: "bg-yellow-100 text-yellow-700" },
  { level: 4, key: "advanced",     color: "bg-blue-100 text-blue-700" },
  { level: 5, key: "expert",       color: "bg-green-100 text-green-700" },
];

const proficiencyInfo = (level, t) => {
  const p = PROFICIENCY.find((x) => x.level === level);
  return p
    ? { label: t(`skillsHub.proficiency.${p.key}`), color: p.color }
    : { label: "—", color: "bg-muted text-muted-foreground" };
};

export default function SkillsHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "catalogue";
  const setTab = (tab) => setSearchParams({ tab });
  const sections = [...new Set(TABS.map((t) => t.section))];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-blue-500" />
            {t("skillsHub.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("skillsHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="border-b">
        {sections.map((section) => (
          <div key={section} className="inline-block mr-6 mb-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3">
              {t(`skillsHub.sections.${section}`)}
            </span>
            {TABS.filter((tb) => tb.section === section).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors mr-1 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(`skillsHub.tabs.${tab.id}`)}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div>
        {activeTab === "catalogue" && <CatalogueTab />}
        {activeTab === "profile"   && <ProfileTab />}
        {activeTab === "matrix"    && <MatrixTab />}
        {activeTab === "gaps"      && <GapsTab />}
      </div>
    </div>
  );
}

// ── Catalogue ─────────────────────────────────────────────────────────────────

function CatalogueTab() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [search, setSearch] = useState("");
  const [selCat, setSelCat] = useState("");
  const [stats, setStats] = useState(null);
  const [showNewSkill, setShowNewSkill] = useState(false);
  const [newSkill, setNewSkill] = useState({ name: "", description: "", category_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [catRes, skillRes, statsRes] = await Promise.all([
      apiClient.get("v1/skills/categories"),
      apiClient.get("v1/skills?active_only=false"),
      apiClient.get("v1/skills/stats"),
    ]);
    setCategories(catRes.data || []);
    setSkills(skillRes.data || []);
    setStats(statsRes.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredSkills = skills.filter((s) => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = !selCat || selCat === "all" || String(s.category_id) === selCat;
    return matchSearch && matchCat;
  });

  const createSkill = async () => {
    if (!newSkill.name.trim()) { toast.error(t("skillsHub.catalogue.nameRequired")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/skills", {
        ...newSkill,
        category_id: newSkill.category_id ? parseInt(newSkill.category_id) : null,
      });
      toast.success(t("skillsHub.catalogue.created"));
      setShowNewSkill(false);
      setNewSkill({ name: "", description: "", category_id: "" });
      load();
    } catch {
      toast.error(t("skillsHub.catalogue.createError"));
    } finally {
      setSaving(false);
    }
  };

  const COLOR_MAP = { blue: "bg-blue-100 text-blue-800", green: "bg-green-100 text-green-800", purple: "bg-purple-100 text-purple-800", orange: "bg-orange-100 text-orange-800", red: "bg-red-100 text-red-800", yellow: "bg-yellow-100 text-yellow-800" };

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label={t("skillsHub.catalogue.statSkills")} value={stats.total_skills} />
          <MiniStat label={t("skillsHub.catalogue.statCategories")} value={stats.total_categories} />
          <MiniStat label={t("skillsHub.catalogue.statAssessments")} value={stats.total_assessments} />
          <MiniStat label={t("skillsHub.catalogue.statEmployeesAssessed")} value={stats.employees_with_skills} />
        </div>
      )}

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8" placeholder={t("skillsHub.catalogue.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={selCat} onValueChange={setSelCat}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder={t("skillsHub.catalogue.allCategories")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("skillsHub.catalogue.allCategories")}</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setShowNewSkill(!showNewSkill)}>
          <Plus className="h-4 w-4 mr-1" /> {t("skillsHub.catalogue.newSkill")}
        </Button>
      </div>

      {showNewSkill && (
        <Card className="border-primary/30">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">{t("skillsHub.catalogue.nameLabel")}</label>
                <Input value={newSkill.name} onChange={(e) => setNewSkill((f) => ({ ...f, name: e.target.value }))} placeholder={t("skillsHub.catalogue.namePlaceholder")} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">{t("skillsHub.catalogue.categoryLabel")}</label>
                <Select value={newSkill.category_id} onValueChange={(v) => setNewSkill((f) => ({ ...f, category_id: v }))}>
                  <SelectTrigger><SelectValue placeholder={t("skillsHub.catalogue.none")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("skillsHub.catalogue.none")}</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">{t("skillsHub.catalogue.descriptionLabel")}</label>
              <Input value={newSkill.description} onChange={(e) => setNewSkill((f) => ({ ...f, description: e.target.value }))} placeholder={t("skillsHub.catalogue.descriptionPlaceholder")} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createSkill} disabled={saving}>{saving ? t("skillsHub.catalogue.creating") : t("skillsHub.catalogue.create")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNewSkill(false)}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped by category */}
      {categories.map((cat) => {
        const catSkills = filteredSkills.filter((s) => s.category_id === cat.id);
        if (!catSkills.length) return null;
        const cls = COLOR_MAP[cat.color] || "bg-gray-100 text-gray-700";
        return (
          <div key={cat.id}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{cat.name}</span>
              <span className="text-xs text-muted-foreground">{t("skillsHub.catalogue.skillsCount", { count: catSkills.length })}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {catSkills.map((skill) => (
                <Card key={skill.id} className={!skill.is_active ? "opacity-40" : ""}>
                  <CardContent className="py-2 px-3">
                    <p className="font-medium text-sm">{skill.name}</p>
                    {skill.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{skill.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {/* Uncategorized */}
      {(() => {
        const uncategorized = filteredSkills.filter((s) => !s.category_id);
        if (!uncategorized.length) return null;
        return (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-muted-foreground">{t("skillsHub.catalogue.uncategorized")}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {uncategorized.map((skill) => (
                <Card key={skill.id}>
                  <CardContent className="py-2 px-3">
                    <p className="font-medium text-sm">{skill.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── My Profile ────────────────────────────────────────────────────────────────

function ProfileTab() {
  const { t } = useTranslation();
  const [mySkills, setMySkills] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ skill_id: "", proficiency_level: "3" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [myRes, allRes] = await Promise.all([
      apiClient.get("v1/skills/me"),
      apiClient.get("v1/skills"),
    ]);
    setMySkills(myRes.data || []);
    setAllSkills(allRes.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSkill = async () => {
    if (!form.skill_id) { toast.error(t("skillsHub.profile.selectSkill")); return; }
    setSaving(true);
    try {
      await apiClient.put("v1/skills/me", {
        skill_id: parseInt(form.skill_id),
        proficiency_level: parseInt(form.proficiency_level),
      });
      toast.success(t("skillsHub.profile.updated"));
      setShowAdd(false);
      setForm({ skill_id: "", proficiency_level: "3" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const removeSkill = async (skillId) => {
    try {
      // find employee id from mySkills
      const es = mySkills.find((s) => s.skill_id === skillId);
      if (!es) return;
      await apiClient.delete(`v1/skills/employees/${es.employee_id}/${skillId}`);
      toast.success(t("skillsHub.profile.removed"));
      load();
    } catch {
      toast.error(t("skillsHub.profile.removeError"));
    }
  };

  const existingIds = new Set(mySkills.map((s) => s.skill_id));
  const availableSkills = allSkills.filter((s) => !existingIds.has(s.id));

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("skillsHub.profile.declaredCount", { count: mySkills.length })}</p>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)}>
          <Plus className="h-4 w-4 mr-1" /> {t("skillsHub.profile.add")}
        </Button>
      </div>

      {showAdd && (
        <Card className="border-primary/30">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">{t("skillsHub.profile.skillLabel")}</label>
                <Select value={form.skill_id} onValueChange={(v) => setForm((f) => ({ ...f, skill_id: v }))}>
                  <SelectTrigger><SelectValue placeholder={t("skillsHub.profile.choose")} /></SelectTrigger>
                  <SelectContent>
                    {availableSkills.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                        {s.category?.name ? ` (${s.category.name})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">{t("skillsHub.profile.levelLabel")}</label>
                <Select value={form.proficiency_level} onValueChange={(v) => setForm((f) => ({ ...f, proficiency_level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROFICIENCY.map((p) => (
                      <SelectItem key={p.level} value={String(p.level)}>{p.level} — {t(`skillsHub.proficiency.${p.key}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={addSkill} disabled={saving}>{saving ? t("skillsHub.profile.saving") : t("skillsHub.profile.save")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mySkills.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("skillsHub.profile.empty")}</p>
            <p className="text-sm">{t("skillsHub.profile.emptyHint")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {mySkills.map((es) => {
            const info = proficiencyInfo(es.proficiency_level, t);
            return (
              <div key={es.id} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-sm">{es.skill?.name}</p>
                  {es.skill?.category?.name && (
                    <p className="text-xs text-muted-foreground">{es.skill.category.name}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <ProficiencyBar level={es.proficiency_level} />
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${info.color}`}>
                    {info.label}
                  </span>
                </div>
                <button
                  onClick={() => removeSkill(es.skill_id)}
                  className="text-muted-foreground hover:text-destructive transition-colors text-xs"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Matrix ────────────────────────────────────────────────────────────────────

function MatrixTab() {
  const { t } = useTranslation();
  const [matrix, setMatrix] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("v1/organization-chart/departments/").then((r) => setDepartments(r.data || [])).catch(() => {});
    loadMatrix();
  }, []);

  const loadMatrix = async (dept = "") => {
    setLoading(true);
    try {
      const url = dept ? `v1/skills/matrix?department_id=${dept}` : "v1/skills/matrix";
      const res = await apiClient.get(url);
      setMatrix(res.data);
    } catch {
      toast.error(t("skillsHub.matrix.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const LEVEL_COLORS = [
    "bg-gray-50",
    "bg-red-100",
    "bg-orange-100",
    "bg-yellow-100",
    "bg-blue-100",
    "bg-green-100",
  ];

  if (loading) return <p className="text-muted-foreground">{t("skillsHub.matrix.loading")}</p>;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <Select value={deptId} onValueChange={(v) => { setDeptId(v); loadMatrix(v); }}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder={t("skillsHub.matrix.allDepartments")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("skillsHub.matrix.allDepartments")}</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => loadMatrix(deptId)}>
          <RefreshCw className="h-3 w-3 mr-1" /> {t("common.refresh")}
        </Button>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {PROFICIENCY.map((p) => (
            <span key={p.level} className={`px-1.5 py-0.5 rounded ${p.color}`}>{p.level} {t(`skillsHub.proficiency.${p.key}`)}</span>
          ))}
        </div>
      </div>

      {!matrix || matrix.employees.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Grid3X3 className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("common.noData")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-r font-medium text-muted-foreground sticky left-0 bg-background w-40">
                  {t("skillsHub.matrix.employee")}
                </th>
                {matrix.skills.map((s) => (
                  <th key={s.id} className="p-2 border-b border-r text-center font-medium max-w-[80px] min-w-[80px]">
                    <div className="truncate" title={s.name}>{s.name}</div>
                    {s.category && <div className="text-muted-foreground font-normal">{s.category}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.employees.map((emp) => (
                <tr key={emp.employee_id} className="hover:bg-muted/30">
                  <td className="p-2 border-b border-r sticky left-0 bg-background">
                    <p className="font-medium">{emp.employee_name}</p>
                    {emp.department && <p className="text-muted-foreground">{emp.department}</p>}
                  </td>
                  {emp.skills.map((s) => (
                    <td key={s.skill_id} className={`p-2 border-b border-r text-center ${LEVEL_COLORS[s.level]}`}>
                      {s.level > 0 ? (
                        <span className="font-bold">{s.level}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Gap Analysis ──────────────────────────────────────────────────────────────

function GapsTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [gap, setGap] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data || []));
  }, []);

  const loadGap = async (empId) => {
    if (!empId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`v1/skills/gap/${empId}`);
      setGap(res.data);
    } catch {
      toast.error(t("skillsHub.gaps.loadError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex gap-3 items-center">
        <Select value={selectedEmp} onValueChange={(v) => { setSelectedEmp(v); loadGap(v); }}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t("skillsHub.gaps.selectEmployee")} />
          </SelectTrigger>
          <SelectContent>
            {employees.map((e) => (
              <SelectItem key={e.id} value={String(e.id)}>
                {e.first_name} {e.last_name}
                {e.department?.name ? ` — ${e.department.name}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <p className="text-muted-foreground">{t("skillsHub.gaps.analyzing")}</p>}

      {gap && !loading && (
        <div className="space-y-4">
          {/* Header */}
          <Card>
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div>
                <p className="font-semibold">{gap.employee_name}</p>
                {gap.job_title && <p className="text-sm text-muted-foreground">{t("skillsHub.gaps.positionLabel", { title: gap.job_title })}</p>}
              </div>
              {gap.has_job && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{gap.coverage_pct}%</p>
                  <p className="text-xs text-muted-foreground">{t("skillsHub.gaps.coverage")}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {!gap.has_job && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
                <p>{t("skillsHub.gaps.noJob")}</p>
              </CardContent>
            </Card>
          )}

          {gap.has_job && (
            <>
              {gap.gaps.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-1 text-red-600">
                    <AlertTriangle className="h-4 w-4" /> {t("skillsHub.gaps.gapsTitle", { count: gap.gaps.length })}
                  </h3>
                  <div className="space-y-2">
                    {gap.gaps.map((g) => (
                      <GapRow key={g.skill_id} entry={g} type="gap" t={t} />
                    ))}
                  </div>
                </div>
              )}

              {gap.covered.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" /> {t("skillsHub.gaps.coveredTitle", { count: gap.covered.length })}
                  </h3>
                  <div className="space-y-2">
                    {gap.covered.map((g) => (
                      <GapRow key={g.skill_id} entry={g} type="covered" t={t} />
                    ))}
                  </div>
                </div>
              )}

              {gap.excess.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-1 text-muted-foreground">
                    <Star className="h-4 w-4" /> {t("skillsHub.gaps.excessTitle", { count: gap.excess.length })}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {gap.excess.map((e) => (
                      <Badge key={e.skill_id} variant="outline" className="text-xs">
                        {e.skill_name} · {e.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!gap && !loading && !selectedEmp && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("skillsHub.gaps.prompt")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function GapRow({ entry, type, t }) {
  const current = proficiencyInfo(entry.current_level, t);
  const required = proficiencyInfo(entry.required_level, t);
  const delta = entry.gap;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${type === "gap" ? "border-red-200 bg-red-50/50" : "border-green-200 bg-green-50/50"}`}>
      <div className="flex-1">
        <p className="font-medium text-sm">{entry.skill_name}</p>
        {entry.is_mandatory && (
          <span className="text-xs text-red-600 font-medium">{t("skillsHub.gaps.mandatory")}</span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs">
        <div className="text-center">
          <p className="text-muted-foreground">{t("skillsHub.gaps.current")}</p>
          <span className={`px-2 py-0.5 rounded-full font-medium ${current.color}`}>
            {entry.current_level > 0 ? current.label : t("skillsHub.gaps.notAssessed")}
          </span>
        </div>
        <div className="text-muted-foreground">→</div>
        <div className="text-center">
          <p className="text-muted-foreground">{t("skillsHub.gaps.required")}</p>
          <span className={`px-2 py-0.5 rounded-full font-medium ${required.color}`}>
            {required.label}
          </span>
        </div>
        {delta > 0 && (
          <Badge variant="destructive" className="text-xs">{t("skillsHub.gaps.levelsDelta", { delta })}</Badge>
        )}
        {delta <= 0 && (
          <CheckCircle className="h-4 w-4 text-green-500" />
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ProficiencyBar({ level }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className={`h-2 w-3 rounded-sm ${n <= level ? "bg-primary" : "bg-muted"}`}
        />
      ))}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value ?? "—"}</p>
      </CardContent>
    </Card>
  );
}
